import type { PromoterRecordPayload } from '@wegic/common';
import type { WegicRequestConfig } from '@wegic/request';
import type {
  IDeleteUserDto,
  INotifyResponse,
  InviteCodeRes,
  IPatchUserInfoDto,
  IProductsResponse,
  ISubscription,
  IUpdateUserSettingDto,
  IUserSetting,
  NotifyExtraParam,
  UserInfoSettings,
} from './const';
import type { UserInfo } from '@/stores/user/types';
import { useRequest } from 'ahooks';
import md5 from 'md5';
import { assetsRequest, userRequest, userRequestV2 } from '@/services/request';
import { obj2FormData } from '@/utils/form-data';

// ==================== 用户账户 ====================

/** 查询已登录用户信息 */
export function getUserInfoRequest(config?: WegicRequestConfig) {
  return userRequest.get<UserInfo>('/accounts', {
    headers: { 'Cache-Control': 'no-cache' },
    ...config,
  });
}

/** 记录推广来源 */
export function recordPromoter(data: PromoterRecordPayload) {
  return assetsRequest.post('/promoter/v1/record', data);
}

/** 更新用户信息 */
export function patchUserInfoRequest(dto: IPatchUserInfoDto) {
  return userRequest.patch<UserInfo>('/accounts', obj2FormData(dto as Record<string, string | File>), {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

/** 退出登录 */
export function logoutRequest() {
  return userRequest.get('/logout');
}

/** 删除用户 */
export function requestDeleteUser(dto: IDeleteUserDto) {
  return userRequest.post('/delete/account', dto);
}

/** 校验当前密码 */
export function requestVerifyPassword(password: string) {
  return userRequest.post<boolean>(
    '/verifyPassword',
    { password: md5(password) },
    {
      hideCodeErrorMessage: true,
      hideHTTPErrorMessage: true,
    },
  );
}

interface ChangeEmailParams {
  email: string;
  newEmail: string;
  password?: string;
}

/** 发送更换邮箱确认邮件 */
export function requestChangeEmail(params: ChangeEmailParams) {
  const data = {
    ...params,
    ...(params.password ? { password: md5(params.password) } : {}),
    time: Date.now(),
    type: 'email',
  };

  return userRequest.post('/changeCertificationMsg/email', data, {
    hideCodeErrorMessage: true,
    hideHTTPErrorMessage: true,
  });
}

/** 确认更换邮箱 */
export function requestConfirmEmailChange(token: string) {
  return userRequest.post<{ succeed: boolean }>(
    '/confirmCertificationMsg/email',
    { token },
    {
      hideCodeErrorMessage: true,
      hideHTTPErrorMessage: true,
    },
  );
}

/** 向指定邮箱发送验证码 */
export function sendEmailVerifyCode(email: string) {
  return userRequest.post('/accounts/verification-codes/email', { usage: 'reset', email });
}

/** 校验邮箱验证码 */
export function verifyEmailCode(email: string, code: string) {
  return userRequest.post(
    '/verifyCode',
    { email, code, type: 'email' },
    {
      hideCodeErrorMessage: true,
      hideHTTPErrorMessage: true,
    },
  );
}

/** 重设账号密码 */
export function resetUserPassword(email: string, password: string) {
  return userRequest.post(
    '/resetUserPassword',
    { email, password: md5(password), type: 'email' },
    {
      hideCodeErrorMessage: true,
      hideHTTPErrorMessage: true,
    },
  );
}

// ==================== 用户设置 ====================

/** 获取用户设置 */
export function getUserInfo(config?: WegicRequestConfig) {
  return userRequest.get<UserInfoSettings>('/settings/user-info', config);
}

/** 获取用户设置（带认证处理） */
export function getAuthUserInfo(pathname?: string) {
  const currentPath = pathname ?? window.location.pathname;
  const isLoginPage = currentPath === '/login';
  const isMaintenance = currentPath.includes('/maintenance');

  return getUserInfo({
    hideCodeErrorMessage: isLoginPage || isMaintenance,
    hideHTTPErrorMessage: isLoginPage || isMaintenance,
  });
}

/** 更新用户语言设置 */
export function updateUserInfoSetting(params: Partial<UserInfoSettings>) {
  return userRequest.post('/settings/user-info', {
    type: 26, // 设置用户配置信息, 非提醒类
    value: params,
  });
}

/** 更新用户设置 */
export function requestUpdateUserSettings<T = unknown>(dto: IUpdateUserSettingDto<T>) {
  return userRequest.post<{ succeed: boolean }>('/settings', dto);
}

/** 获取用户设置列表 */
export function getUserSettingsRequest(config?: WegicRequestConfig) {
  return userRequest.get<IUserSetting[]>('/settings', config);
}

// ==================== 订阅/产品 ====================

/** 获取产品列表 */
export function getProductsRequest(config?: WegicRequestConfig) {
  return userRequest.get<IProductsResponse>('/product', config);
}

/** 获取用户权益/订阅信息 */
export function getUserSubscriptionRequest(config?: WegicRequestConfig) {
  return assetsRequest.get<ISubscription>('/integral/v1/user/subscription', config);
}

/** 自动续订订阅/取消自动续订 */
export function patchSubscriptionRequest(operate: 'uncancel' | 'cancel') {
  return assetsRequest.patch('/integral/v1/user/subscription', { operate });
}

/** 创建订阅/积分包结算，返回 clientSecret 或 autoPay 结果 */
export function postSubscriptionRequest(data: {
  lineItem: { productId: string; priceId: string; quantity: number };
  source?: string;
  successUrl?: string;
  cancelUrl?: string;
  redirectOnCompletion?: 'NEVER';
  language?: string;
}) {
  return assetsRequest.post<{ clientSecret?: string; autoPay?: boolean; url?: string }>(
    '/integral/v1/user/subscription',
    data,
    { hideCodeErrorMessage: true },
  );
}

// ==================== 通知 ====================

/** 获取用户通知列表 */
export function getUserNotifyList(config?: WegicRequestConfig) {
  return userRequest.get<INotifyResponse>('/notify', config);
}

/** 更新单个通知状态 */
export function updateUserNotify(name: string, config?: WegicRequestConfig) {
  return userRequest.post<{ succeed: boolean }>(`/notify/${name}`, {}, config);
}

export function requestPrepayCtaShow() {
  return userRequest.post('/crm/prepay-cta-show', {}, {
    hideCodeErrorMessage: true,
    hideHTTPErrorMessage: true,
  });
}

/** 查询单个通知状态 */
export function queryUserNotify(type: string, extraParam?: NotifyExtraParam[]) {
  return userRequestV2.post<{ needNotified: boolean }>('/queryNotify', { type, extraParam });
}

/** 更新单个通知状态（带参数） */
export function updateUserNotifyV2(type: string, extraParam?: NotifyExtraParam[]) {
  return userRequestV2.post<{ succeed: boolean }>('/notify', { type, extraParam });
}

// ==================== 邀请码 ====================

/** 邀请码信息 Hook */
export function useInviteCodeInfo(inviteCode: string, refreshDep?: number) {
  const { data, loading, error } = useRequest(
    () => userRequest.get<InviteCodeRes>(`/inviteCode/${inviteCode}`),
    {
      refreshDeps: [refreshDep],
      ready: !!inviteCode,
    },
  );
  return {
    data: data?.data,
    loading,
    error,
  };
}
